import {
	BSDeliveryWsOptions,
	MessageHandler,
	SendHandler,
	WsDeliveryDebugLogger,
} from './types';
import {
	WS_CLOSE_INTENTIONAL,
	WS_DEFAULT_MAX_RECONNECT_COUNT,
	WS_MAX_RECONNECT_DELAY_MS,
	WS_RAPID_RECONNECT_WINDOW_MS,
	WS_RECONNECT_BACKOFF_MULTIPLIER,
	WS_RECONNECT_LINEAR_STEP_MS_DEFAULT,
	WS_RECONNECT_RAPID_BASE_MS_DEFAULT,
} from './constants/common';
import { wsCloseCode } from './helpers/common';
import WebSocketService from './webSocketService/webSocketService';
import { Notification, NotificationType } from './webSocketService/WebSocket';

class BigStateWsDeliveryClient {
	private baseUrl: string = '';
	private _apiKey: string = '';
	private _token: string = '';
	private socket: WebSocketService | null = null;
	private reconnectCount = 0;
	private maxReconnectCount = WS_DEFAULT_MAX_RECONNECT_COUNT;
	private messages: Set<string> = new Set<string>([]);
	private messageHandlers: MessageHandler[] = [];
	private timerId: ReturnType<typeof setTimeout> | null = null;
	private disconnectRequestedByUser = false;
	/** First unintentional close since last successful open — start of rapid-reconnect clock. */
	private outageStartedAtMs: number | null = null;
	/** Rapid phase: exponentially growing spaced delay until WS_MAX_RECONNECT_DELAY_MS */
	private aggressiveCurrentDelayMs = WS_RECONNECT_RAPID_BASE_MS_DEFAULT;
	private rapidReconnectBaseMs = WS_RECONNECT_RAPID_BASE_MS_DEFAULT;
	/** Long phase: nth schedule after the rapid window waits `n × linearReconnectStepMs`. */
	private linearReconnectStepMs = WS_RECONNECT_LINEAR_STEP_MS_DEFAULT;
	private reconnectBackoffAttempt = 1;
	private debugLog?: WsDeliveryDebugLogger;

	constructor(data: BSDeliveryWsOptions) {
		this.baseUrl = `${data.baseUrl}/v1`;
		const rapidBase = data.reconnectTime ?? WS_RECONNECT_RAPID_BASE_MS_DEFAULT;
		this.rapidReconnectBaseMs = rapidBase;
		this.aggressiveCurrentDelayMs = rapidBase;
		this.linearReconnectStepMs =
			data.reconnectLinearStepMs ?? WS_RECONNECT_LINEAR_STEP_MS_DEFAULT;
		const { maxReconnectCount, token, apiKey } = data;
		token && (this._token = token);
		apiKey && (this._apiKey = apiKey);
		maxReconnectCount && (this.maxReconnectCount = maxReconnectCount);

		const dbg = data.debug;
		if (dbg === true) {
			this.debugLog = (msg, ...rest) =>
				console.log('[BigState WS Delivery]', msg, ...rest);
		} else if (typeof dbg === 'function') {
			this.debugLog = dbg;
		}
	}

	set apiKey(value: string) {
		this._apiKey = value;
	}

	set token(value: string) {
		this._token = value;
	}

	private get authKey() {
		return { apiKey: this._apiKey, token: this._token };
	}

	onOpenSocket(send: SendHandler, objects: string[]) {
		send(objects);
	}

	addDeliveries(deliveries: string[]) {
		if (!deliveries.length) {
			return;
		}
		this.messages = new Set([...this.messages, ...deliveries]);
		this.trace('addDeliveries', {
			added: deliveries.length,
			totalTracked: this.messages.size,
			hasSocket: !!this.socket,
		});
		if (!this.socket) {
			this.openSocket();
			return;
		}
		this.socket.send(deliveries);
	}

	setDeliveries(deliveries: string[]) {
		this.messages = new Set(deliveries);
		this.trace('setDeliveries', {
			count: deliveries.length,
			totalTracked: this.messages.size,
			hasSocket: !!this.socket,
		});
		if (!this.messages.size) {
			this.clearDeliveries();
			return;
		}
		if (!this.socket) {
			this.openSocket();
			return;
		}
		this.socket.send(deliveries);
	}

	clearDeliveries() {
		this.trace('clearDeliveries');
		this.messages.clear();
		this.disconnectRequestedByUser = true;
		this.closeSocketAndTimers();
	}

	addMessageHandler(handler: MessageHandler) {
		if (this.messageHandlers.includes(handler)) {
			this.trace('addMessageHandler skipped duplicate');
			return;
		}
		this.messageHandlers.push(handler);
		this.trace('addMessageHandler', {
			totalHandlers: this.messageHandlers.length,
		});
	}

	setMessageHandlers(handlers: MessageHandler[]) {
		this.messageHandlers = [...handlers];
		this.trace('setMessageHandlers', {
			totalHandlers: this.messageHandlers.length,
		});
	}

	removeMessageHandler(handler: MessageHandler) {
		const before = this.messageHandlers.length;
		this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
		this.trace('removeMessageHandler', {
			removed: before - this.messageHandlers.length,
			totalHandlers: this.messageHandlers.length,
		});
	}

	clearMessageHandlers() {
		this.trace('clearMessageHandlers');
		this.messageHandlers = [];
	}

	disconnect() {
		this.trace('disconnect');
		this.disconnectRequestedByUser = true;
		this.messages.clear();
		this.messageHandlers = [];
		this.closeSocketAndTimers();
	}

	private scheduleReconnectAfterClose(): void {
		const now = Date.now();
		if (this.outageStartedAtMs === null) {
			this.outageStartedAtMs = now;
		}
		const inRapidPhase =
			now - this.outageStartedAtMs < WS_RAPID_RECONNECT_WINDOW_MS;

		if (inRapidPhase) {
			if (!this.reconnectCount) {
				void this.retry('webSocket close');
			}
			this.scheduleAggressiveBackoff();
		} else {
			this.scheduleLinearBackoff();
		}
		this.trace('scheduleReconnectAfterClose', {
			inRapidPhase,
			reconnectCount: this.reconnectCount,
			outageAgeMs:
				this.outageStartedAtMs === null ? null : now - this.outageStartedAtMs,
		});
	}

	private trace(message: string, ...args: unknown[]): void {
		this.debugLog?.(message, ...args);
	}

	private scheduleAggressiveBackoff(): void {
		if (this.timerId) {
			clearTimeout(this.timerId);
			this.timerId = null;
		}

		const delayMs = Math.round(
			Math.min(this.aggressiveCurrentDelayMs, WS_MAX_RECONNECT_DELAY_MS),
		);
		this.aggressiveCurrentDelayMs = Math.round(
			Math.min(
				WS_MAX_RECONNECT_DELAY_MS,
				this.aggressiveCurrentDelayMs * WS_RECONNECT_BACKOFF_MULTIPLIER,
			),
		);

		this.trace('scheduleAggressiveBackoff', {
			delayMs,
			nextDelayCeilingMs: this.aggressiveCurrentDelayMs,
		});

		this.timerId = setTimeout(() => {
			this.timerId = null;
			if (this.reconnectCount) {
				void this.retry('webSocket close');
			}
		}, delayMs);
	}

	private scheduleLinearBackoff(): void {
		if (this.timerId) {
			clearTimeout(this.timerId);
			this.timerId = null;
		}

		const delayMs = Math.round(
			this.linearReconnectStepMs * this.reconnectBackoffAttempt,
		);
		this.reconnectBackoffAttempt += 1;

		this.trace('scheduleLinearBackoff', {
			delayMs,
			nextLinearMultiplier: this.reconnectBackoffAttempt,
		});

		this.timerId = setTimeout(() => {
			this.timerId = null;
			void this.retry('webSocket close');
		}, delayMs);
	}

	retry(error: any) {
		this.reconnectCount += 1;
		this.trace('retry invoked', {
			reconnectCount: this.reconnectCount,
			maxReconnectCount: this.maxReconnectCount,
		});
		if (this.reconnectCount > this.maxReconnectCount) {
			this.handleNotification({
				type: NotificationType.Error,
				data: { error },
			});
			this.trace('retry stopped: maxReconnectCount exceeded', {
				maxReconnectCount: this.maxReconnectCount,
			});
			if (this.timerId) {
				clearTimeout(this.timerId);
				this.timerId = null;
			}
			this.messageHandlers = [];
			this.messages = new Set<string>();
			this.reconnectBackoffAttempt = 1;
			this.outageStartedAtMs = null;
			this.aggressiveCurrentDelayMs = this.rapidReconnectBaseMs;
			this.reconnectCount = 0;
			this.disconnectRequestedByUser = false;
			return;
		}
		this.closeSocketAndTimers();
		if (!(this.reconnectCount > this.maxReconnectCount)) {
			this.openSocket();
		}
	}

	openSocket() {
		const deliveries = Array.from(this.messages);
		this.trace('openSocket', {
			deliveriesCount: deliveries.length,
			baseUrl: this.baseUrl,
		});
		const options = {
			baseUrlWs: this.baseUrl,
			authKeys: this.authKey,
			debugLog: this.debugLog,
			onOpen: (send: SendHandler) => {
				this.reconnectCount = 0;
				this.disconnectRequestedByUser = false;
				this.outageStartedAtMs = null;
				this.aggressiveCurrentDelayMs = this.rapidReconnectBaseMs;
				this.reconnectBackoffAttempt = 1;
				if (this.timerId) {
					clearTimeout(this.timerId);
					this.timerId = null;
				}
				this.onOpenSocket(send, Array.from(this.messages));
				this.trace('delivery onOpen', {
					deliveries: Array.from(this.messages),
				});
				this.handleNotification({
					type: NotificationType.Info,
					data: { message: 'webSocket opened' },
				});
			},
			onMessage: (message: Notification) => {
				this.handleNotification(message);
			},
			onError: (message: Notification) => {
				this.trace('delivery onError', message);
				this.handleNotification(message);
			},
			onClose: (event: number | CloseEvent) => {
				const code = wsCloseCode(event);
				const userClosed = this.disconnectRequestedByUser;
				const intentional = userClosed || code === WS_CLOSE_INTENTIONAL;

				this.trace('delivery onClose', {
					code,
					userClosed,
					intentional,
				});
				this.handleNotification({
					type: NotificationType.Info,
					data: {
						message: 'webSocket closed',
					},
				});
				if (!intentional) {
					this.scheduleReconnectAfterClose();
				} else {
					this.disconnectRequestedByUser = false;
					this.closeSocketAndTimers();
					if (userClosed) {
						this.reconnectCount = 0;
						this.outageStartedAtMs = null;
						this.aggressiveCurrentDelayMs = this.rapidReconnectBaseMs;
						this.reconnectBackoffAttempt = 1;
					}
				}
			},
		};
		this.socket = new WebSocketService(options);
	}

	private handleNotification(message: Notification): void {
		const handlers = new Set(this.messageHandlers);
		for (const handler of handlers) {
			handler(message);
		}
	}

	private closeSocketAndTimers() {
		this.trace('closeSocketAndTimers');
		if (this.timerId) {
			clearTimeout(this.timerId);
			this.timerId = null;
		}
		if (this.socket) {
			this.socket.close();
			this.socket = null;
		}
	}

	isSocketConnected(): boolean {
		return !!this.socket;
	}
}

export default BigStateWsDeliveryClient;
